/**
 * The GF switcher — the account's companions as an avatar list (most-recent-first
 * ordering is done upstream by the store), selecting one swaps the active
 * girlfriend → her stream loads. Purely presentational: companions + active id in,
 * a select event out. Reused verbatim in the desktop sidebar and the mobile drawer.
 */
import { Avatar, Spinner, useT } from '@4f/ui';
import { For, type JSX, Show } from 'solid-js';
import type { CompanionView } from '../../api/types.ts';

export interface SwitcherProps {
  /** Switcher-ready companions (visible, most-recently-active first). */
  readonly companions: readonly CompanionView[];
  readonly activeId: string | null;
  readonly loading: boolean;
  readonly onSelect: (companionId: string) => void;
  /** Resolve a companion's avatar URL (`avatarAssetId` → e2 URL), or `undefined` (initials fallback). */
  readonly avatarSrc: (companionId: string) => string | undefined;
  /**
   * DOM id for the heading. The shell renders this panel TWICE (a persistent
   * desktop aside + the mobile drawer), so each instance must carry its own id —
   * duplicate ids are invalid HTML and make any `aria-labelledby` pointing here
   * ambiguous. It also lets the drawer name itself from this heading.
   */
  readonly headingId?: string;
}

export function Switcher(props: SwitcherProps): JSX.Element {
  const { t } = useT();

  return (
    <nav class="f4-switcher" aria-label={t('chat.switcher.title')}>
      <h2 id={props.headingId} class="f4-switcher__title">
        {t('chat.switcher.title')}
      </h2>
      <Show
        when={!props.loading || props.companions.length > 0}
        fallback={<Spinner label={t('surface.loadingCompanions')} />}
      >
        <Show
          when={props.companions.length > 0}
          fallback={<p class="f4-switcher__empty">{t('chat.switcher.empty')}</p>}
        >
          <ul class="f4-switcher__list">
            <For each={props.companions}>
              {(companion) => (
                <li>
                  <button
                    type="button"
                    class="f4-switcher__item"
                    aria-current={companion.id === props.activeId ? 'true' : undefined}
                    onClick={() => props.onSelect(companion.id)}
                  >
                    <Avatar
                      alt={companion.name}
                      name={companion.name}
                      src={props.avatarSrc(companion.id)}
                      size="md"
                    />
                    <span class="f4-switcher__name">{companion.name}</span>
                  </button>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>
    </nav>
  );
}
